import { useEffect, useContext, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { toast } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

import CreatableSelect from "react-select/creatable";
import ToastDisplay from "../components/ToastDisplay";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Pagination from "../components/Pagination";
import PageLoader from "../components/PageLoader";
import Empty from "../components/Empty";
import FileUpload from "../components/FileUpload";

const Students = () => {
	const navigate = useNavigate();
	const photoRef = useRef(null);
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [students, setStudents] = useState([]);
	const [meta, setMeta] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams({});
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [filtermodal, setFilterModal] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [studentModal, setStudentModal] = useState(false);
	const [levelLoading, setLevelLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [levels, setLevels] = useState([]);

	const [name, setName] = useState("");
	const [level, setLevel] = useState({});
	const [studentNo, setStudentNo] = useState("");
	const [rfidNo, setRfidNo] = useState("");
	const [msisdn, setMsisdn] = useState("");
	const [photo, setPhoto] = useState(null);
	const [photoPath, setPhotoPath] = useState("");

	const [searchQ, setSearchQ] = useState(searchParams.get("q") || "");
	const [searchLevel, setSearchLevel] = useState(
		searchParams.get("level") || ""
	);

	const searchFields = [
		{ key: "q", value: searchQ },
		{ key: "level", value: searchLevel },
	];

	const filterFields = [
		{
			key: "level",
			value: searchLevel,
			defaultVal: "",
			edit: setSearchLevel,
		},
	];

	const requiredFields = [
		{ key: "name", value: name },
		{ key: "studentNo", value: studentNo },
		{ key: "rfidNo", value: rfidNo },
		{ key: "msisdn", value: msisdn },
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: { "X-Auth-Token": session },
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/students`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setStudents(data.students);
			setMeta(data.meta);
			setLevels(
				data.levels.map((level) => ({
					value: level.name,
					label: level.name,
				}))
			);
		}
		setLoading(false);
	};

	const search = (e) => {
		e.preventDefault();
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "") {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
		setFilterModal(false);
	};

	const removeFilter = (e, func, key) => {
		e.preventDefault();
		func("");
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "" && field.key !== key) {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
	};

	const clearFilters = (e) => {
		e.preventDefault();
		setSearchParams({});
		filterFields.map((field) => {
			field.edit(field.defaultVal);
		});
	};

	const closeStudentModal = () => {
		setStudentModal(false);
		setName("");
		setLevel({});
		setStudentNo("");
		setRfidNo("");
		setMsisdn("");
		setPhoto(null);
		setPhotoPath("");
		setErrors([]);
	};

	const isEmpty = (value) => {
		return value === "" || value === null || value === undefined;
	};

	const saveStudent = async () => {
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				name: name,
				student_no: studentNo,
				id_no: rfidNo,
				level: level?.value || "",
				msisdn: msisdn,
				photo: photoPath,
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/students/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			closeStudentModal();
			navigate(`/students/${data.student_id}`);
		}
		setActionLoading(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setActionLoading(true);
		var newErrors = [];

		requiredFields.map((field) => {
			if (!field.value || field.value === "" || field.value.length === 0) {
				newErrors.push(field.key);
			}
		});

		if (newErrors.length !== 0) {
			toast(
				<ToastDisplay
					status="error"
					content="Please fill out all required fields."
				/>
			);
			setActionLoading(false);
			setErrors(newErrors);
			return;
		}
		saveStudent();
	};

	const handlePhoto = (e) => {
		const file = e.target.files[0];
		const newUid = uuidv4();
		const uuid_name = `${newUid}.${file.name.split(".").pop()}`;

		const forUpload = {
			uuid: uuid_name,
			file: file,
		};
		setPhoto(forUpload);
		setPhotoPath(
			`${process.env.REACT_APP_STORAGE_URL}/pisara/students/${uuid_name}`
		);
	};

	const createLevel = async (inputValue) => {
		setLevelLoading(true);
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				levels: [inputValue],
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/students/levels/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			setLevels([...levels, { value: inputValue, label: inputValue }]);
			setLevel({ value: inputValue, label: inputValue });
		}
		setLevelLoading(false);
	};

	useEffect(() => {
		document.title = "Students | Pisara Dashboard";
		if (profile) {
			if (!profile.role.students_view) {
				navigate("/");
			} else {
				getData();
				if (filterFields.every((field) => isEmpty(field.value))) {
					setShowFilters(false);
				} else {
					setShowFilters(true);
				}
			}
		}
	}, [searchParams, profile]);

	return (
		<>
			<Container className="gx-5">
				<Row className="content-header flex-row-left mb-20">
					<Col>
						<h1 className="text-xl m-0">Students</h1>
					</Col>
				</Row>
				<Row className="flex-row-left mb-20">
					<Col className="col-5">
						<Form onSubmit={search}>
							<div className="search-container bg-white flex-row-left">
								<i className="ri-search-line search-icon" />
								<Form.Control
									className="text-sm"
									placeholder="Search students"
									value={searchQ}
									onChange={(e) => setSearchQ(e.target.value)}
								/>
								<Button
									variant="light"
									className="filter-btn flex-column"
									onClick={() => setFilterModal(true)}
								>
									<i className="ri-equalizer-3-fill m-0" />
								</Button>
							</div>
						</Form>
					</Col>
					<Col className="col-7 flex-row-right">
						<Pagination
							meta={meta}
							searchParams={searchParams}
							setSearchParams={setSearchParams}
							searchFields={searchFields}
							className="pull-right flex-row-left"
						/>
						<Button
							variant="light"
							className={`refresh-btn flex-column ${
								!profile?.role?.students_write && "no-margin"
							}`}
							onClick={getData}
						>
							<i className="ri-restart-line opacity-7" />
						</Button>
						{profile?.role?.students_write && (
							<Button
								className="control-btn btn-sm btn-default text-medium-gotham text-sm"
								onClick={() => setStudentModal(true)}
							>
								<i className="ri-user-add-line" />
								Add Student
							</Button>
						)}
					</Col>
				</Row>
				<Row className="content-table-header bg-white gx-0">
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">Name</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Student No.
						</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							RFID No.
						</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Grade/Level
						</span>
					</Col>
				</Row>
				{loading ? (
					<PageLoader className="bg-white p-50" />
				) : students?.length === 0 ? (
					<Empty label="No students to show." className="bg-white" />
				) : (
					students.map((student) => (
						<Link key={student.id} to={`/students/${student.id}`}>
							<Row className="content-table-item bg-white gx-0">
								<Col className="p-10 ellipsis">
									<span className="text-sm">{student.name}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{student.student_no}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{student.id_no}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{student.level}</span>
								</Col>
							</Row>
						</Link>
					))
				)}
			</Container>
			<Modal
				show={studentModal}
				className="info-modal admin-content"
				centered
				size="lg"
				onHide={closeStudentModal}
			>
				<Form onSubmit={handleSubmit}>
					<Modal.Header>
						<Modal.Title className="text-lg">Add Student</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-30 full flex-column">
							<div className="file-upload-container">
								{photo && (
									<FileUpload
										file={photo}
										files={photo}
										setFiles={setPhoto}
										filePaths={photoPath}
										setFilePaths={setPhotoPath}
										directory="pisara/students"
									/>
								)}
								{!photo && (
									<div
										className="file-btn border rounded-8"
										onClick={() => photoRef.current.click()}
									>
										<div className="overlay" />
										<img
											src="/default-user.png"
											alt=""
											className="default-user-photo"
										/>
									</div>
								)}
								<input
									ref={photoRef}
									type="file"
									className="hidden"
									accept="image/*"
									onChange={handlePhoto}
								/>
							</div>
						</div>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								Student Information
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Name</Form.Label>
								<Form.Control
									autoFocus
									className={`text-sm form-input ${
										errors.includes("name") && "border-danger"
									}`}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</Col>
						</Row>
						<Row className="mb-50">
							<Col>
								<Form.Label className="text-sm">Student No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("studentNo") && "border-danger"
									}`}
									value={studentNo}
									onChange={(e) => setStudentNo(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">Grade/Level</Form.Label>
								<CreatableSelect
									isClearable
									isDisabled={levelLoading}
									isLoading={levelLoading}
									onCreateOption={createLevel}
									value={level}
									onChange={setLevel}
									options={levels}
									menuShouldScrollIntoView={false}
									maxMenuHeight={200}
									placeholder=""
									styles={{
										control: (baseStyles, state) => ({
											...baseStyles,
											height: "38px",
											outline: "none",
											borderRadius: 4,
											fontSize: "0.875rem",
											borderColor: state.isFocused
												? "#86b7fe !important"
												: "#dee2e6 !important",
											boxShadow: state.isFocused
												? "0 0 0 .25rem rgba(13, 110, 253, .25)"
												: "none",
										}),
										option: (baseStyles, state) => ({
											...baseStyles,
											fontSize: ".875rem",
											color: "#050911",
											backgroundColor: state.isFocused
												? "#c8deff !important"
												: "#fff",
										}),
									}}
								/>
							</Col>
						</Row>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								Notification Settings
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">
									Guardian's Mobile No.
								</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("msisdn") && "border-danger"
									}`}
									value={msisdn}
									onChange={(e) => setMsisdn(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">RFID No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("rfidNo") && "border-danger"
									}`}
									value={rfidNo}
									onChange={(e) => setRfidNo(e.target.value)}
								/>
							</Col>
						</Row>
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="outline-secondary"
							className="btn-sm action-btn text-sm text-medium-inter me-2"
							onClick={closeStudentModal}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							className="btn-default btn-sm action-btn flex-column text-sm text-medium-inter"
							disabled={actionLoading}
						>
							{actionLoading ? (
								<Spinner className="btn-loader text-white" />
							) : (
								"Save"
							)}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
		</>
	);
};

export default Students;
